import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { bff } from '~/lib/bff';
import { useUser } from '~/modules/auth/hooks/useUser';
import { RemotesContext } from '~/modules/remotes/contexts/RemotesContext.ts';

import type { IRemoteContext } from '../types';

interface RemoteContextMap {
  [path: string]: {
    context: IRemoteContext;
    isolateStyles: boolean;
  };
}

function useRemoteContexts() {
  const { name, hasPermission, email } = useUser();
  const { remotes } = useContext(RemotesContext);
  const { i18n } = useTranslation();

  return useMemo(
    () =>
      remotes.reduce<RemoteContextMap>((map, remote) => {
        map[remote.name] = {
          context: {
            locale: i18n.language,
            name: remote.name,
            prefix: remote.prefix,
            refreshAuthToken: bff.refreshAuthToken,
            user: {
              name,
              hasPermission,
              email,
            },
          },
          isolateStyles: remote.isolateStyles ?? false,
        };

        return map;
      }, {}),
    [hasPermission, name, email, remotes, i18n.language],
  );
}

export { useRemoteContexts };
export type { RemoteContextMap };
